import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { tenantsApi, type TenantFull, ApiError, type LifecycleState } from '../api/client';
import { LifecycleBadge, BillingBadge } from '../components/StatusBadge';

type Action = 'reactivate' | 'suspend' | 'archive' | 'offboard' | 'delete';

const ACTIONS_BY_STATE: Record<LifecycleState, Action[]> = {
  PENDING: ['reactivate', 'archive', 'offboard'],
  ACTIVE: ['suspend', 'archive', 'offboard'],
  SUSPENDED: ['reactivate', 'archive', 'offboard'],
  ARCHIVED: ['offboard', 'delete'],
  OFFBOARDED: ['delete'],
  DELETED: [],
};

const ACTION_LABEL: Record<Action, string> = {
  reactivate: 'Reactivate',
  suspend: 'Suspend',
  archive: 'Archive',
  offboard: 'Offboard',
  delete: 'Delete (hard)',
};

const ACTION_DESTRUCTIVE: Record<Action, boolean> = {
  reactivate: false,
  suspend: true,
  archive: true,
  offboard: true,
  delete: true,
};

const ACTION_CONFIRM: Record<Action, string> = {
  reactivate: 'Bring this tenant back to ACTIVE?',
  suspend: 'Suspend this tenant? Logins will be blocked until reactivation.',
  archive: 'Archive this tenant? It becomes read-only.',
  offboard:
    'Begin offboarding? Tenant data is preserved during the export window before hard deletion.',
  delete:
    'HARD DELETE this tenant? This drops the tenant DB and purges R2. Cannot be undone.',
};

export function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantFull | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<Action | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setTenant(null);
    setLoadError(null);
    (async () => {
      try {
        const r = await tenantsApi.get(id);
        if (!cancelled) setTenant(r.tenant);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.body.message ?? 'Failed to load' : 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (!id) return null;

  if (loadError) {
    return (
      <div className="space-y-3">
        <Link to="/tenants" className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">← Tenants</Link>
        <p className="text-bad">{loadError}</p>
      </div>
    );
  }

  if (!tenant) return <p className="text-sm text-ink-muted">Loading…</p>;

  async function performAction(action: Action) {
    setActionError(null);
    if (!confirm(ACTION_CONFIRM[action])) return;
    setBusyAction(action);
    try {
      let next;
      if (action === 'reactivate') next = await tenantsApi.reactivate(tenant!.id);
      else if (action === 'suspend') next = await tenantsApi.suspend(tenant!.id);
      else if (action === 'archive') next = await tenantsApi.archive(tenant!.id);
      else if (action === 'offboard') next = await tenantsApi.offboard(tenant!.id);
      else next = await tenantsApi.delete(tenant!.id);

      setTenant(next.tenant);
      // If the tenant was just hard-deleted, send the user back to the list.
      if (next.tenant.lifecycle_state === 'DELETED') {
        navigate('/tenants');
      }
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Action failed' : 'Action failed',
      );
    } finally {
      setBusyAction(null);
    }
  }

  const availableActions = ACTIONS_BY_STATE[tenant.lifecycle_state];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/tenants" className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">
          ← Tenants
        </Link>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{tenant.display_name}</h1>
            <p className="text-sm text-ink-muted font-mono mt-1">
              {tenant.slug}.auremhealth.dev
              {tenant.custom_domain && (
                <span> · custom: <span className="text-ink">{tenant.custom_domain}</span></span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <LifecycleBadge state={tenant.lifecycle_state} />
            <BillingBadge status={tenant.billing_status} />
          </div>
        </div>
      </div>

      {actionError && <p role="alert" className="text-sm text-bad">{actionError}</p>}

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">Identity</h2>
        <DefList rows={[
          ['ID', <code className="text-xs text-ink-muted">{tenant.id}</code>],
          ['Slug', <code className="text-xs">{tenant.slug}</code>],
          ['Display name', tenant.display_name],
          ['Custom domain', tenant.custom_domain ?? '—'],
          ['Timezone', tenant.timezone],
          ['D1 database ID', <code className="text-xs text-ink-muted">{tenant.d1_database_id}</code>],
          ['R2 prefix', <code className="text-xs">{tenant.r2_prefix}</code>],
        ]} />
      </section>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">Lifecycle</h2>
        <DefList rows={[
          ['State', <LifecycleBadge state={tenant.lifecycle_state} />],
          ['Onboarded at', tenant.onboarded_at ?? '—'],
          ['Suspended at', tenant.suspended_at ?? '—'],
          ['Archived at', tenant.archived_at ?? '—'],
          ['Offboarded at', tenant.offboarded_at ?? '—'],
          ['Deleted at', tenant.deleted_at ?? '—'],
          ['Created at', tenant.created_at],
          ['Updated at', tenant.updated_at],
        ]} />

        {availableActions.length > 0 && (
          <div className="border-t border-surface-3 pt-4 flex flex-wrap gap-2">
            {availableActions.map((a) => (
              <button
                key={a}
                onClick={() => performAction(a)}
                disabled={busyAction !== null}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                  ACTION_DESTRUCTIVE[a]
                    ? 'bg-bad/15 text-bad hover:bg-bad/25'
                    : 'bg-good/15 text-good hover:bg-good/25'
                }`}
              >
                {busyAction === a ? `${ACTION_LABEL[a]}…` : ACTION_LABEL[a]}
              </button>
            ))}
          </div>
        )}
      </section>

      {(tenant.lifecycle_state === 'PENDING' || tenant.lifecycle_state === 'ACTIVE') && (
        <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg">Provisioning</h2>
              <p className="text-xs text-ink-muted mt-1">
                {tenant.lifecycle_state === 'PENDING'
                  ? 'Create the tenant database and seed it from the accepted brand kit + offerings.'
                  : 'Invite the first Clinic Admin so the tenant can take over from here.'}
              </p>
            </div>
            <Link
              to={`/tenants/${tenant.id}/provisioning`}
              className="rounded-md bg-primary/15 px-3 py-1.5 text-xs uppercase tracking-wider text-primary hover:bg-primary/25"
            >
              {tenant.lifecycle_state === 'PENDING' ? 'Provision & onboard' : 'Invite Clinic Admin'}
            </Link>
          </div>
        </section>
      )}

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg">Branding</h2>
          <Link
            to={`/tenants/${tenant.id}/brand-extraction`}
            className="rounded-md bg-primary/15 px-3 py-1.5 text-xs uppercase tracking-wider text-primary hover:bg-primary/25"
          >
            Extract from URL or PDF
          </Link>
        </div>
        <DefList rows={[
          ['Default colour mode', tenant.brand_default_color_mode],
          ['Primary colour', colorChip(tenant.brand_primary_color)],
          ['Secondary colour', colorChip(tenant.brand_secondary_color)],
          ['Accent colour', colorChip(tenant.brand_accent_color)],
          ['Heading font', tenant.brand_font_heading ?? '—'],
          ['Body font', tenant.brand_font_body ?? '—'],
          ['Logo R2 key', codeOrDash(tenant.brand_logo_r2_key)],
          ['Wordmark R2 key', codeOrDash(tenant.brand_wordmark_r2_key)],
        ]} />
      </section>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg">Clinic offerings</h2>
          <Link
            to={`/tenants/${tenant.id}/offerings-extraction`}
            className="rounded-md bg-primary/15 px-3 py-1.5 text-xs uppercase tracking-wider text-primary hover:bg-primary/25"
          >
            Extract from URL / PDF / CSV
          </Link>
        </div>
        <p className="text-xs text-ink-muted">
          The catalog of services + products this clinic offers, used by AI Treatment Plans.
          Accepted offerings will be inserted into the tenant DB during provisioning (Phase 0.5).
        </p>
      </section>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">Email sender</h2>
        <DefList rows={[
          ['From name', tenant.email_from_name ?? '—'],
          ['From address', tenant.email_from_address ?? '—'],
          ['Reply-to', tenant.email_reply_to ?? '—'],
          ['SPF verified', verifiedLabel(tenant.email_spf_verified)],
          ['DKIM verified', verifiedLabel(tenant.email_dkim_verified)],
          ['DMARC verified', verifiedLabel(tenant.email_dmarc_verified)],
          ['Last verified', tenant.email_last_verified_at ?? '—'],
        ]} />
        <p className="text-xs text-ink-muted border-t border-surface-3 pt-3">
          Email sender configuration UI ships in Phase 0.5.
        </p>
      </section>
    </div>
  );
}

function DefList({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-ink-muted text-xs uppercase tracking-wider self-center">{k}</dt>
          <dd className="text-ink break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function colorChip(hex: string | null) {
  if (!hex) return <>—</>;
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="inline-block w-4 h-4 rounded border border-surface-3"
        style={{ backgroundColor: hex }}
      />
      <code className="text-xs">{hex}</code>
    </span>
  );
}

function codeOrDash(s: string | null) {
  return s ? <code className="text-xs">{s}</code> : <>—</>;
}

function verifiedLabel(n: number) {
  return n ? <span className="text-good">Verified</span> : <span className="text-ink-muted">Not verified</span>;
}
